import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import api from "../utils/api";
import LiveStrip from "../components/LiveStrip";
import ReviewsBar from "../components/ReviewsBar";
import EmaLiveOpsSection from "../components/ema/EmaLiveOpsSection";
import "../styles/parkingVsTaxi.css";

export default function HowToGetToEastMidlandsAirport() {
  const DEFAULT_PICKUP = "NG1 5FS";
  const DESTINATION_POSTCODE = "DE74 2SA";
  const DEFAULT_FLIGHT_NUMBER = "FR8683";

  const TERMINAL_LABEL = "East Midlands Airport (DE74 2SA)";

  const computeTomorrowAt10 = () => {
    const now = new Date();
    const t = new Date(now);
    t.setDate(now.getDate() + 1);
    t.setHours(10, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}T${pad(t.getHours())}:${pad(t.getMinutes())}`;
  };

  const [form, setForm] = useState(() => ({
    pickup_postcode: DEFAULT_PICKUP,
    destination_postcode: DESTINATION_POSTCODE,
    adults: 2,
    children_5_15: 0,
    infants_0_4: 0,
    luggage: 2,
    flight_number: DEFAULT_FLIGHT_NUMBER,
    seniors_60_plus: false,
    special_assistance: false,
    pushchair: false,
    pickup_datetime: computeTomorrowAt10(),
    parking_duration_days: 4,
  }));

  const [pickupSuggestions, setPickupSuggestions] = useState([]);
  const [pickupWarning, setPickupWarning] = useState("");
  const [pickupFocused, setPickupFocused] = useState(false);
  const pickupTimer = useRef(null);

  const [flightSuggestions, setFlightSuggestions] = useState([]);
  const [flightFocused, setFlightFocused] = useState(false);
  const flightTimer = useRef(null);
  const [flightLookup, setFlightLookup] = useState(null);
  const [flightLookupError, setFlightLookupError] = useState(null);
  const flightLookupTimer = useRef(null);
  const lastFlightLookup = useRef("");

  const [sectionOpen, setSectionOpen] = useState({
    leave: false,
    selfDrive: false,
    rail: false,
    taxi: false,
    hassle: false,
    how: false,
  });
  const sectionRefs = {
    leave: useRef(null),
    selfDrive: useRef(null),
    rail: useRef(null),
    taxi: useRef(null),
  };

  const [loading, setLoading] = useState(false);
  const [loadingSteps, setLoadingSteps] = useState([]);
  const [loadingSlow, setLoadingSlow] = useState(false);
  const loadingTimers = useRef([]);

  const [result, setResult] = useState(null);
  const [parkingQuote, setParkingQuote] = useState(null);
  const [driveQuote, setDriveQuote] = useState(null);
  const [error, setError] = useState(null);
  const autoSubmitted = useRef(false);

  useEffect(() => {
    if (autoSubmitted.current) return;
    if (form.pickup_postcode && form.destination_postcode && form.pickup_datetime) {
      autoSubmitted.current = true;
      submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.pickup_postcode, form.destination_postcode, form.pickup_datetime]);

  const isLikelyUkPostcode = (value) => {
    if (!value) return false;
    const norm = String(value).toUpperCase().trim();
    const pcRegex = /^([A-Z]{1,2}[0-9][0-9A-Z]?|[A-Z][0-9][A-Z]|[A-Z]{2}[0-9]{2})\s*[0-9][A-Z]{2}$/;
    return pcRegex.test(norm);
  };

  const fetchPickupSuggestions = (value) => {
    const v = String(value || "");
    if (pickupTimer.current) clearTimeout(pickupTimer.current);
    if (v.length < 2) {
      setPickupSuggestions([]);
      setPickupWarning("");
      return;
    }
    if (v.length >= 4 && !isLikelyUkPostcode(v)) {
      setPickupWarning("Please enter a valid UK postcode (e.g. NG1 5FS).");
    } else {
      setPickupWarning("");
    }
    pickupTimer.current = setTimeout(async () => {
      try {
        const resp = await api.get("/api/postcodes/suggest", { params: { q: v, limit: 10 } });
        const suggestions = resp.data?.suggestions || [];
        setPickupSuggestions(suggestions.map((pc) => ({ postcode: pc, label: "Suggested" })));
      } catch (e) {
        setPickupSuggestions([]);
      }
    }, 180);
  };

  const resetLoadingIndicators = () => {
    loadingTimers.current.forEach((t) => clearTimeout(t));
    loadingTimers.current = [];
    setLoadingSteps([]);
    setLoadingSlow(false);
  };

  const fetchFlightSuggestions = (value) => {
    const v = String(value || "");
    if (flightTimer.current) clearTimeout(flightTimer.current);
    if (v.length < 2) {
      setFlightSuggestions([]);
      return;
    }
    flightTimer.current = setTimeout(async () => {
      try {
        const resp = await api.get("/api/ema/flights/suggest", { params: { q: v, limit: 8 } });
        setFlightSuggestions(resp.data?.suggestions || []);
      } catch (e) {
        setFlightSuggestions([]);
      }
    }, 180);
  };

  const lookupFlight = async (flight) => {
    const clean = String(flight || "").trim();
    if (!clean) return;
    try {
      setFlightLookupError(null);
      const resp = await api.get("/api/ema/flights/lookup", { params: { flight: clean } });
      setFlightLookup(resp.data?.data || null);
    } catch (e) {
      setFlightLookup(null);
      setFlightLookupError("Could not fetch flight timing right now.");
    }
  };

  useEffect(() => {
    const v = String(form.flight_number || "").trim();
    if (!v || v === lastFlightLookup.current) return;
    if (flightLookupTimer.current) clearTimeout(flightLookupTimer.current);
    flightLookupTimer.current = setTimeout(() => {
      lookupFlight(v);
      lastFlightLookup.current = v;
    }, 240);
    return () => {
      if (flightLookupTimer.current) clearTimeout(flightLookupTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.flight_number]);

  const startLoadingIndicators = () => {
    resetLoadingIndicators();
    const steps = [
      { id: "distance", label: "Checking distance & time", status: "done" },
      { id: "taxi", label: "Checking taxi & ride-hail", status: "pending" },
      { id: "tfl", label: "Checking TfL/rail options", status: "pending" },
      { id: "nr", label: "Checking National Rail", status: "pending" },
      { id: "compare", label: "Comparing cost & hassle", status: "pending" },
    ];
    setLoadingSteps(steps);
    loadingTimers.current.push(
      setTimeout(() => setLoadingSteps((prev) => prev.map((s) => (s.id === "taxi" ? { ...s, status: "done" } : s))), 500),
      setTimeout(() => setLoadingSteps((prev) => prev.map((s) => (s.id === "tfl" ? { ...s, status: "done" } : s))), 900),
      setTimeout(() => setLoadingSteps((prev) => prev.map((s) => (s.id === "nr" ? { ...s, status: "done" } : s))), 1300),
      setTimeout(() => setLoadingSteps((prev) => prev.map((s) => (s.id === "compare" ? { ...s, status: "done" } : s))), 1600),
      setTimeout(() => setLoadingSlow(true), 1800)
    );
  };

  const submit = async () => {
    if (pickupWarning || !isLikelyUkPostcode(form.pickup_postcode)) {
      setPickupWarning("Please enter a valid UK postcode (e.g. NG1 5FS).");
      setError("Please enter a valid pickup postcode.");
      return;
    }
    setLoading(true);
    setError(null);
    setParkingQuote(null);
    setDriveQuote(null);
    startLoadingIndicators();

    try {
      const passengers = Math.min(8, Math.max(1, Number(form.adults || 0) + Number(form.children_5_15 || 0)));
      const parkingDays = Math.min(60, Math.max(1, Number(form.parking_duration_days) || 0));
      const payload = {
        ...form,
        passengers,
        parking_duration_days: parkingDays,
        pickup_postcode: form.pickup_postcode.replace(/\(.*?\)/g, "").trim(),
        luggage: Math.min(8, Math.max(0, Number(form.luggage) || 0)),
        destination_postcode: DESTINATION_POSTCODE,
        destination_locked: true,
        children: Number(form.children_5_15 || 0),
        infants: Number(form.infants_0_4 || 0),
        seniors: !!form.seniors_60_plus,
        special_assistance: !!form.special_assistance,
        pushchair: !!form.pushchair,
      };
      const comparePromise = api.post("/api/train/compare", payload);
      const parkingPromise = api.get("/api/parking/quote", {
        params: {
          airport: "EMA",
          daysStay: parkingDays,
        },
      });
      const drivePromise = api.get("/api/drive/quote", {
        params: {
          from: payload.pickup_postcode,
          to: DESTINATION_POSTCODE,
          mpg: 45,
          mpgType: "UK",
          fuelPricePerLitre: 1.45,
          includeReturn: false,
        },
      });

      const [compareRes, parkingRes, driveRes] = await Promise.allSettled([
        comparePromise,
        parkingPromise,
        drivePromise,
      ]);

      if (compareRes.status === "fulfilled") {
        setResult(compareRes.value.data);
      } else {
        throw compareRes.reason;
      }

      if (parkingRes.status === "fulfilled" && parkingRes.value?.data) {
        setParkingQuote(parkingRes.value.data);
      } else {
        setParkingQuote(null);
      }

      if (driveRes.status === "fulfilled" && driveRes.value?.data) {
        setDriveQuote(driveRes.value.data);
      } else {
        setDriveQuote(null);
      }
    } catch (err) {
      setError("Failed to calculate comparison. Please try again.");
    }

    setLoading(false);
    resetLoadingIndicators();
  };

  const renderMoney = (v) => (v == null ? "—" : `£${Number(v).toFixed(2)}`);

  const apiData = result?.data || {};
  const options = apiData.options || [];
  const meta = apiData.meta || {};
  const providers = meta.providers || {};
  const legacyCosts = result?.costs || {};
  const journey = result?.journey || {};
  const pax = journey?.passengers ?? ((Number(form.adults || 0) + Number(form.children_5_15 || 0)) || 1);

  const ensureTrainTotal = (opt) => {
    if (!opt) return null;
    if (typeof opt.total_cost_gbp === "number" && !Number.isNaN(opt.total_cost_gbp)) return opt;

    const b = opt.breakdown || {};
    const totalFallbacks = [
      b.fare_total_gbp,
      b.total_cost_gbp,
      b.total_fare_gbp,
      b.total,
    ];
    for (const t of totalFallbacks) {
      const n = Number(t);
      if (Number.isFinite(n)) return { ...opt, total_cost_gbp: n };
    }

    const per = Number(b.fare_per_person_gbp);
    if (Number.isFinite(per)) {
      return { ...opt, total_cost_gbp: Number((per * pax).toFixed(2)) };
    }

    const range = b.range || {};
    if (Number.isFinite(range.mid)) return { ...opt, total_cost_gbp: Number(range.mid) };
    if (Number.isFinite(range.min)) return { ...opt, total_cost_gbp: Number(range.min) };

    return opt;
  };

  const taxiOption = options.find((o) => o.id === "taxi_ccg") || null;
  const rideHailRange = providers?.ride_hail?.range || providers?.ride_hail?.breakdown?.range || null;
  const rideHailMid = rideHailRange?.mid ?? providers?.ride_hail?.mid ?? null;
  const rideHailOption =
    options.find((o) => o.id === "ride_hail_estimate") ||
    (rideHailMid != null
      ? {
          id: "ride_hail_estimate",
          label: "Ride-hail (estimate)",
          mode: "taxi",
          total_cost_gbp: Number(rideHailMid),
          breakdown: { range: rideHailRange || providers.ride_hail },
        }
      : null);
  const blackCabRange = providers?.black_cab?.range || providers?.black_cab?.breakdown?.range || null;
  const blackCabMid = blackCabRange?.mid ?? providers?.black_cab?.mid ?? null;
  const blackCabOption =
    options.find((o) => o.id === "black_cab_estimate") ||
    (blackCabMid != null
      ? {
          id: "black_cab_estimate",
          label: "Black cab (estimate)",
          mode: "taxi",
          total_cost_gbp: Number(blackCabMid),
          breakdown: { range: blackCabRange || providers.black_cab, note: providers.black_cab_note || null },
        }
      : null);
  const tflOption = ensureTrainTotal(options.find((o) => o.id === "tfl") || null);
  const nrOption = ensureTrainTotal(options.find((o) => o.id === "national_rail") || null);
  const parkingData = parkingQuote?.data || null;
  const bestParking =
    (parkingData?.gold && parkingData.gold[0]) ||
    (parkingData?.silver && parkingData.silver[0]) ||
    (parkingData?.bronze && parkingData.bronze[0]) ||
    (parkingData?.all && parkingData.all[0]) ||
    null;
  const parkingPrice = bestParking && bestParking.price_gbp != null ? Number(bestParking.price_gbp) : null;
  const parkingTransferMinutes =
    bestParking?.transfer_minutes ??
    bestParking?.walk_minutes ??
    bestParking?.shuttle_minutes ??
    (typeof bestParking?.transfer_time === "string" ? Number(bestParking.transfer_time.match(/(\d{1,3})/)?.[1]) : null);
  const driveData = driveQuote?.data || null;
  const driveFuelCost = driveData?.fuel?.fuelCost != null ? Number(driveData.fuel.fuelCost) : null;
  const driveDurationMinutes = driveData?.durationMin ?? null;
  const selfDriveTotal =
    parkingPrice != null && driveFuelCost != null ? Number((Number(parkingPrice) + Number(driveFuelCost)).toFixed(2)) : null;
  const selfDriveOption =
    selfDriveTotal != null
      ? {
          id: "self_drive",
          label: "Self-drive (fuel + parking)",
          mode: "drive",
          total_cost_gbp: selfDriveTotal,
          breakdown: {
            duration_minutes: driveDurationMinutes ?? null,
            drive_km: driveData?.km ?? null,
            fuel_cost_gbp: driveFuelCost,
            parking_price_gbp: parkingPrice,
            parking_product: bestParking || null,
            walk_minutes: parkingTransferMinutes ?? null,
            transfer_type: bestParking?.transfer_type || null,
          },
        }
      : null;

  const walkFor = (opt) => {
    if (!opt) return 0;
    if (opt.mode === "taxi") return 0;
    const directWalk = opt.breakdown?.walk_minutes ?? opt.breakdown?.transfer_minutes;
    const combinedWalk = (opt.breakdown?.access_walk_mins || 0) + (opt.breakdown?.egress_walk_mins || 0);
    if (directWalk != null) return directWalk;
    if (combinedWalk) return combinedWalk;
    return journey?.total_walk_minutes ?? 0;
  };
  const changesFor = (opt) => {
    if (!opt) return 0;
    if (opt.mode === "taxi" || opt.mode === "drive") return 0;
    return opt.breakdown?.interchanges ?? journey?.interchanges ?? 0;
  };
  const durationFor = (opt) => {
    if (!opt) return null;
    if (opt.mode === "drive") return opt.breakdown?.duration_minutes ?? null;
    return opt.breakdown?.duration_minutes ?? meta.taxi_duration_mins ?? journey?.duration_minutes ?? null;
  };
  const hassleFor = (opt) => {
    const walk = walkFor(opt);
    const changes = changesFor(opt);
    const bus = (opt?.breakdown?.price_only_reasons || []).includes("includes_bus") || opt?.breakdown?.includes_bus;
    const luggage = Number(form.luggage || 0);
    let score = 0;
    score += (changes || 0) * 2;
    if (bus) score += 1;
    score += Math.ceil((walk || 0) / 5);
    if (luggage >= 2) score += 1;
    if (score >= 6) return { label: "High hassle", emoji: "🔴" };
    if (score >= 3) return { label: "Medium hassle", emoji: "🟠" };
    return { label: "Low hassle", emoji: "🟢" };
  };

  const candidateOptions = [selfDriveOption, tflOption, nrOption, taxiOption, rideHailOption, blackCabOption].filter(Boolean);
  const rankedOptions = candidateOptions
    .filter((o) => typeof o.total_cost_gbp === "number" && !Number.isNaN(o.total_cost_gbp))
    .sort((a, b) => a.total_cost_gbp - b.total_cost_gbp);
  const cheapestOption = rankedOptions[0] || null;
  const runnerUp = rankedOptions[1] || null;

  const cheapestFriction = (opt) => {
    if (!opt) return { friction: false, reasons: [] };
    const reasons = [];
    const walk = walkFor(opt);
    const changes = changesFor(opt);
    const duration = durationFor(opt);
    const taxiDuration = meta.taxi_duration_mins;
    const priceReasons = opt.breakdown?.price_only_reasons || [];
    if (priceReasons.includes("includes_bus")) reasons.push("🚌 includes bus");
    if (walk >= 15 || priceReasons.includes("long_walk")) reasons.push(`🚶 ${walk || 15}+ min walk`);
    if (changes >= 3 || priceReasons.includes("many_changes")) reasons.push(`🔁 ${changes} changes`);
    if (duration && taxiDuration && duration > 1.5 * taxiDuration) reasons.push("⏱ slower than taxi");
    return { friction: reasons.length > 0, reasons };
  };

  const cheapestMeta = cheapestFriction(cheapestOption);
  const bestOverall = cheapestMeta.friction && runnerUp ? runnerUp : cheapestOption;
  const savingVsBest =
    cheapestOption && bestOverall && cheapestOption.id !== bestOverall.id
      ? Number((bestOverall.total_cost_gbp - cheapestOption.total_cost_gbp).toFixed(2))
      : null;

  const durationLabel = (opt) => {
    if (!opt) return "—";
    const duration = durationFor(opt);
    if (opt.mode === "drive" && duration != null) {
      return `${duration} min drive`;
    }
    return duration != null ? `${duration} min` : "—";
  };

  const formatLocalTime = (isoString) => {
    if (!isoString) return null;
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return null;
    const date = d.toLocaleDateString("en-GB", { timeZone: "Europe/London", day: "2-digit", month: "short" });
    const time = d.toLocaleTimeString("en-GB", { timeZone: "Europe/London", hour: "2-digit", minute: "2-digit" });
    return `${date} • ${time} UK`;
  };

  const formatRelativeLabel = (isoString, baseIso) => {
    if (!isoString) return null;
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return null;
    const base = baseIso ? new Date(baseIso) : null;
    const time = d.toLocaleTimeString("en-GB", { timeZone: "Europe/London", hour: "2-digit", minute: "2-digit" });
    const hour = d.toLocaleTimeString("en-GB", { timeZone: "Europe/London", hour: "2-digit", hour12: false }).slice(0, 2);
    const hNum = Number(hour);

    const partOfDay =
      hNum < 5
        ? "early morning"
        : hNum < 12
        ? "morning"
        : hNum < 17
        ? "afternoon"
        : hNum < 21
        ? "evening"
        : "late night";

    let dayNote = "";
    if (base) {
      const baseDate = new Date(base.toLocaleString("en-US", { timeZone: "Europe/London" }));
      const curDate = new Date(d.toLocaleString("en-US", { timeZone: "Europe/London" }));
      const diffDays = Math.round((curDate - baseDate) / (24 * 3600 * 1000));
      if (diffDays === 1) dayNote = "next day";
      else if (diffDays === -1) dayNote = "previous day";
      else if (diffDays !== 0) dayNote = `${Math.abs(diffDays)} days ${diffDays > 0 ? "later" : "earlier"}`;
    }

    const tag = dayNote ? `${partOfDay}, ${dayNote}` : partOfDay;
    return { label: `${time} (${tag})`, crossesDay: !!dayNote };
  };

  const bestTravelMinutes = () => {
    const optionsWithDur = [
      { opt: taxiOption, dur: durationFor(taxiOption) },
      { opt: selfDriveOption, dur: durationFor(selfDriveOption) },
      { opt: tflOption, dur: durationFor(tflOption) },
      { opt: nrOption, dur: durationFor(nrOption) },
    ]
      .map(({ opt, dur }) => ({ opt, dur: Number.isFinite(dur) ? Number(dur) : null }))
      .filter(({ dur }) => dur !== null && dur > 0);
    if (!optionsWithDur.length) return { minutes: null, option: null };
    const best = optionsWithDur.reduce((a, b) => (a.dur <= b.dur ? a : b));
    return { minutes: best.dur, option: best.opt };
  };

  const leaveHomeAdvice = (() => {
    const data = flightLookup || null;
    const arrivalBy = data?.airport_arrival_by_utc || null;
    if (!arrivalBy) return null;
    const travel = bestTravelMinutes();
    const travelMins = travel?.minutes;
    if (!travelMins) return null;
    const leaveTime = new Date(arrivalBy);
    leaveTime.setMinutes(leaveTime.getMinutes() - travelMins);
    const scheduled = data?.scheduled_departure_utc || data?.scheduled_arrival_utc || null;
    const arrivalRel = formatRelativeLabel(arrivalBy, scheduled || arrivalBy);
    const leaveRel = formatRelativeLabel(leaveTime.toISOString(), arrivalBy);
    const scheduledRel = scheduled ? formatRelativeLabel(scheduled, arrivalBy) : null;
    const crossMidnight =
      (arrivalRel && arrivalRel.crossesDay) ||
      (scheduledRel && scheduledRel.crossesDay) ||
      (leaveRel && leaveRel.crossesDay);
    return {
      airportBy: arrivalRel?.label || formatLocalTime(arrivalBy),
      leaveBy: leaveRel?.label || formatLocalTime(leaveTime.toISOString()),
      travelMins,
      travelMode: travel?.option?.mode || null,
      travelLabel: travel?.option?.label || null,
      bufferMins: data?.buffer_minutes ?? 0,
      direction: data?.direction || "departure",
      targetSource: data?.airport_arrival_source || data?.target_source || null,
      scheduled: scheduledRel?.label || formatLocalTime(scheduled),
      crossesMidnight: !!crossMidnight,
      terminal: data?.terminal || null,
    };
  })();

  const displayHassle = (opt, hassle) => {
    if (!hassle) return null;
    if (opt?.mode === "taxi") return { ...hassle, label: "Lowest hassle" };
    if (opt?.mode === "drive") return { ...hassle, label: "Moderate hassle (parking + walk)" };
    if (opt?.mode === "train") return { ...hassle, label: "Moderate hassle (transfer involved)" };
    return hassle;
  };

  const renderHero = () => {
    const medals = ["GOLD", "SILVER", "BRONZE"];
    const palette = {
      GOLD: { bg: "#0b132b", text: "#5eead4", border: "#14b8a6", emoji: "🥇" },
      SILVER: { bg: "#111827", text: "#e5e7eb", border: "#cbd5e1", emoji: "🥈" },
      BRONZE: { bg: "#0b132b", text: "#fb923c", border: "#fb923c", emoji: "🥉" },
    };

    const tiles = medals.map((medal, idx) => {
      const opt = rankedOptions[idx] || null;
      const paletteFor = palette[medal] || palette.GOLD;
      const duration = durationFor(opt);
      const walk = walkFor(opt);
      const changes = changesFor(opt);
      const bus =
        (opt?.breakdown?.price_only_reasons || []).includes("includes_bus") || opt?.breakdown?.includes_bus;
      const label = opt?.id === "taxi_ccg" ? "Taxi" : opt?.label || "—";
      const perPerson =
        opt?.mode === "train" && opt?.breakdown?.fare_per_person_gbp != null
          ? `(${renderMoney(opt.breakdown.fare_per_person_gbp)} pp × ${pax})`
          : null;
      const walkText = (() => {
        if (!opt) return "—";
        if (opt.mode === "taxi") return "🚪 Door-to-terminal";
        if (opt.mode === "drive") {
          const transferType = (opt.breakdown?.transfer_type || "").toLowerCase();
          const transferIcon = transferType.includes("shuttle") ? "🚌" : "🚶";
          return walk ? `${transferIcon} ${walk} min walk from parking` : "🚗 Drive + park";
        }
        if (bus || (changes && changes > 0) || (walk && walk > 0)) return "🚉 Station + transfer required";
        return "🚉 Direct rail";
      })();
      const note =
        opt?.id === "national_rail"
          ? "🚌 Includes transfer from East Midlands Parkway"
          : opt?.id === "self_drive" && opt?.breakdown?.parking_product?.product_name
          ? `🚗 Parking: ${opt.breakdown.parking_product.product_name}`
          : bus
          ? "🚌 Includes bus segments"
          : opt?.source && String(opt.source).includes("model")
          ? "⚠ price estimate"
          : null;
      const hassle = opt ? displayHassle(opt, hassleFor(opt)) : null;

      return (
        <div
          key={medal}
          style={{
            border: `1px solid ${paletteFor.border}`,
            borderRadius: 14,
            padding: 14,
            background: paletteFor.bg,
            color: paletteFor.text,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <div style={{ fontWeight: 800, fontSize: 13, letterSpacing: 0.5 }}>
            {paletteFor.emoji} {medal}
          </div>
          <div style={{ fontSize: 17, fontWeight: 800, marginTop: 6, color: "#fff" }}>{label}</div>
          <div style={{ fontSize: 16, fontWeight: 800, marginTop: 4, color: "#fff" }}>
            {opt ? renderMoney(opt.total_cost_gbp) : "—"} {perPerson}
          </div>
          {opt && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6, fontSize: 12, color: "#e5e7eb", flex: 1 }}>
              <span>{`⏱ ${durationLabel(opt)}`}</span>
              <span>{walkText}</span>
              {hassle && <span>{hassle.emoji} {hassle.label}</span>}
              {note && <span style={{ color: "#5eead4" }}>{note}</span>}
              <div style={{ flexGrow: 1 }} />
              <div
                style={{
                  marginTop: 10,
                  display: "flex",
                  justifyContent: "flex-start",
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    const mode = opt?.mode || "";
                    const openAndScroll = (key) => {
                      setSectionOpen((prev) => ({ ...prev, [key]: true }));
                      requestAnimationFrame(() => {
                        const el = sectionRefs[key]?.current;
                        if (el && el.scrollIntoView) {
                          el.scrollIntoView({ behavior: "smooth", block: "start" });
                        }
                      });
                    };
                    if (mode === "drive") openAndScroll("selfDrive");
                    else if (mode === "taxi") openAndScroll("taxi");
                    else if (mode === "train") openAndScroll("rail");
                  }}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#e5e7eb",
                    textDecoration: "underline",
                    fontSize: 12,
                    padding: 0,
                    cursor: "pointer",
                  }}
                >
                  View details ▸
                </button>
              </div>
            </div>
          )}
        </div>
      );
    });

    const cheapestLine = cheapestOption ? `${cheapestOption.label} at ${renderMoney(cheapestOption.total_cost_gbp)}` : "Not available";
    const bestLine = bestOverall ? `${bestOverall.label} at ${renderMoney(bestOverall.total_cost_gbp)}` : "Not available";

    return (
      <div
        style={{
          borderRadius: 18,
          padding: 18,
          marginBottom: 16,
          color: "#fff",
          background: "linear-gradient(120deg, #0b132b 0%, #14b8a6 100%)",
          boxShadow: "0 10px 25px rgba(0,0,0,0.12)",
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 13, letterSpacing: 0.4, textTransform: "uppercase", opacity: 0.9 }}>
          Best way to East Midlands Airport
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>
          Best overall: {bestLine}
        </div>
        {bestOverall && cheapestOption && bestOverall.id !== cheapestOption.id && (
          <div style={{ fontSize: 15, fontWeight: 700, marginTop: 6, opacity: 0.95 }}>
            Cheapest price: {cheapestLine}
            {cheapestMeta.friction && cheapestMeta.reasons.length > 0 && (
              <span style={{ color: "#fbbf24", display: "block", marginTop: 4 }}>
                Cheapest but slower: {cheapestMeta.reasons.join(" • ")}
              </span>
            )}
          </div>
        )}
        {Number.isFinite(savingVsBest) && bestOverall && cheapestOption && bestOverall.id !== cheapestOption.id && (
          <div style={{ fontSize: 12, marginTop: 6, opacity: 0.9 }}>
            🚩 Cheapest saves about £{Math.abs(savingVsBest)} vs best overall — check the hassle before picking it.
          </div>
        )}
        <div style={{ display: "flex", gap: 10, marginTop: 10, fontSize: 12, fontWeight: 700, flexWrap: "wrap" }}>
          <span>💷 Cheapest</span>
          <span>🧳 Bags + transfers shown</span>
          <span>⚖️ Best overall marked Gold</span>
        </div>
        <div
          style={{
            marginTop: 12,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 10,
          }}
        >
          {tiles}
        </div>
      </div>
    );
  };

  const renderFareCards = () => {
    const optionRow = (title, opt, extra) => {
      if (!opt) return null;
      const range = opt.breakdown?.range;
      const note = opt.breakdown?.note;
      return (
        <div className="train-fare-row" style={{ padding: "10px 12px" }}>
          <div className="train-fare-label" style={{ fontWeight: 700, fontSize: 13 }}>{title}</div>
          <div className="train-fare-value strong" style={{ fontSize: 14 }}>
            {range ? `${renderMoney(range.min)} – ${renderMoney(range.max)}` : renderMoney(opt.total_cost_gbp)}
            {extra && <div className="train-fare-sub" style={{ fontSize: 12 }}>{extra}</div>}
            {note && <div className="train-fare-sub" style={{ fontSize: 12 }}>{note}</div>}
          </div>
        </div>
      );
    };

    const tflPerPerson =
      tflOption?.breakdown?.fare_per_person_gbp != null ? `(${renderMoney(tflOption.breakdown.fare_per_person_gbp)} per person × ${pax})` : null;
    const nrPerPerson =
      nrOption?.breakdown?.fare_per_person_gbp != null ? `(${renderMoney(nrOption.breakdown.fare_per_person_gbp)} per person × ${pax})` : null;
    const parkingLabel =
      bestParking?.product_name || bestParking?.product_key
        ? `${bestParking.product_name || bestParking.product_key}${bestParking.transfer_type ? ` • ${bestParking.transfer_type}` : ""}`
        : null;

    return (
      <div className="train-fare-grid" style={{ gap: 12, display: "grid" }}>
        {selfDriveOption && (
          <details
            className="train-collapsible"
            open={sectionOpen.selfDrive}
            onToggle={(e) => setSectionOpen((prev) => ({ ...prev, selfDrive: e.target.open }))}
            ref={sectionRefs.selfDrive}
          >
            <summary className="train-summary-bar">🚗 Self-drive (fuel + parking)</summary>
            <div className="train-collapsible-body">
              <div className="train-fare-group" style={{ background: "#ecfdf3", borderRadius: 12, padding: 10, fontSize: 13, border: "1px solid #bbf7d0" }}>
                <div className="train-fare-row" style={{ padding: "10px 12px" }}>
                  <div className="train-fare-label" style={{ fontWeight: 700, fontSize: 13 }}>Fuel + parking</div>
                  <div className="train-fare-value strong" style={{ fontSize: 14 }}>
                    {renderMoney(selfDriveOption.total_cost_gbp)}
                    <div className="train-fare-sub" style={{ fontSize: 12 }}>
                      Fuel: {renderMoney(driveFuelCost)} • Parking: {renderMoney(parkingPrice)}
                      {parkingLabel && <> ({parkingLabel}, typical)</>}
                    </div>
                    {parkingData?.found != null && (
                      <div className="train-fare-sub" style={{ fontSize: 12, color: "#166534" }}>
                        {parkingData.found} parking options found; showing best value.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </details>
        )}

        <details
          className="train-collapsible"
          open={sectionOpen.rail}
          onToggle={(e) => setSectionOpen((prev) => ({ ...prev, rail: e.target.open }))}
          ref={sectionRefs.rail}
        >
          <summary className="train-summary-bar">🚆 Rail options</summary>
          <div className="train-collapsible-body">
            <div className="train-fare-group" style={{ background: "#f8fafc", borderRadius: 12, padding: 10, fontSize: 13 }}>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>
                Rail fares are per person; totals here assume {pax} traveller{pax === 1 ? "" : "s"} (defaults to 1).
              </div>
              {optionRow("TfL (where available)", tflOption, tflPerPerson)}
              {optionRow("National Rail (via EMA Parkway + transfer)", nrOption, nrPerPerson)}
            </div>
          </div>
        </details>

        <details
          className="train-collapsible"
          open={sectionOpen.taxi}
          onToggle={(e) => setSectionOpen((prev) => ({ ...prev, taxi: e.target.open }))}
          ref={sectionRefs.taxi}
        >
          <summary className="train-summary-bar">🚖 Taxi & Ride-hail</summary>
          <div className="train-collapsible-body">
            <div className="train-fare-group" style={{ background: "#f8fafc", borderRadius: 12, padding: 10, fontSize: 13 }}>
              {optionRow("CheapCabGrab (fixed)", taxiOption)}
              {optionRow("Ride-hail (estimate)", rideHailOption)}
              {optionRow("Black cab (estimate)", blackCabOption)}
              <div className="train-fare-row muted-row" style={{ padding: "8px 10px", fontSize: 12 }}>
                <div className="train-fare-label">Note</div>
                <div className="train-fare-value muted">Taxi is door-to-terminal and includes drop-off.</div>
              </div>
            </div>
          </div>
        </details>
      </div>
    );
  };

  const renderLoading = () => {
    if (!loading && !loadingSteps.length) return null;
    return (
      <div className="parking-results" style={{ marginTop: 12 }}>
        <div className="train-summary-bar" style={{ background: "#0b132b", color: "#fff" }}>
          Crunching the best way to East Midlands Airport…
        </div>
        <div style={{ padding: 12, background: "#fff", border: "1px solid #e5e7eb", borderTop: 0 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {loadingSteps.map((s) => (
              <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                <span>{s.status === "done" ? "✅" : "⏳"}</span>
                <span>{s.label}</span>
              </div>
            ))}
          </div>
          {loadingSlow && (
            <div style={{ marginTop: 8, fontSize: 12, color: "#b45309" }}>
              Live fares can take a few seconds — thanks for waiting.
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="parking-wrapper">
      <Helmet>
        <title>How to Get to East Midlands Airport (2026)</title>
        <meta
          name="description"
          content="Door-to-terminal planner for East Midlands Airport: taxi vs train + Parkway transfer vs (coming soon) drive, with live fares and hassle scoring."
        />
        <link rel="canonical" href="https://www.cheapcabgrab.com/how-to-get-to-east-midlands-airport" />
        <meta property="og:title" content="How to Get to East Midlands Airport (2026)" />
        <meta
          property="og:description"
          content="Find the best way to East Midlands Airport with live taxi fares, National Rail data, and transfer notes from Parkway."
        />
        <meta property="og:url" content="https://www.cheapcabgrab.com/how-to-get-to-east-midlands-airport" />
      </Helmet>

      <LiveStrip variant="airports_general" />

      <div className="parking-header">
        <h1>How to Get to East Midlands Airport (Live 2026)</h1>
        <div style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 8px", background: "#ecfeff", border: "1px solid #a5f3fc", borderRadius: 999, fontSize: 11, fontWeight: 700, color: "#0f172a" }}>
          Updated: 21st Apr 2026 • Door-to-terminal comparison
        </div>
        <div style={{ marginTop: 10 }}>
          <a
            href="/east-midlands-airport/live-journey-planner"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 12px",
              borderRadius: 999,
              background: "#eff6ff",
              border: "1px solid #93c5fd",
              color: "#1d4ed8",
              fontSize: 12,
              fontWeight: 800,
              textDecoration: "none",
            }}
          >
            Need live monitoring? Try the Live Journey Planner
          </a>
        </div>
        <div style={{ fontSize: 11, color: "#475467", marginTop: 6 }}>
          Based on live train fares (National Rail & TfL), real journey times, and current taxi pricing. Some non-bookable fares (e.g. ride-hail) are estimated.
          <div style={{ fontSize: 10, color: "#475467" }}>Live rail data provided under National Rail DRM licence.</div>
        </div>
      </div>

      <div className="parking-form">
        <div className="form-group" style={{ marginBottom: 10 }}>
          <label>Pickup postcode</label>
          <input
            className="parking-input"
            value={form.pickup_postcode}
            placeholder="e.g. NG1 5FS"
            onChange={(e) => {
              setForm({ ...form, pickup_postcode: e.target.value });
              fetchPickupSuggestions(e.target.value);
            }}
            onFocus={() => setPickupFocused(true)}
            onBlur={() => setTimeout(() => setPickupFocused(false), 120)}
            style={{ padding: "9px 10px", fontSize: 14 }}
          />
          {pickupWarning && <div style={{ color: "#b91c1c", fontSize: 12, marginTop: 4 }}>{pickupWarning}</div>}
          {pickupFocused && pickupSuggestions.length > 0 && (
            <div className="suggestion-box">
              {pickupSuggestions.map((s, idx) => (
                <div
                  key={`${s.postcode}-${idx}`}
                  className="suggestion-item"
                  onClick={() => {
                    setForm({ ...form, pickup_postcode: s.postcode });
                    setPickupFocused(false);
                    setPickupSuggestions([]);
                  }}
                >
                  <div className="suggestion-main">{s.postcode}</div>
                  <div className="suggestion-sub">{s.label || "Suggested"}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-group" style={{ marginBottom: 10 }}>
          <label>Parking duration</label>
          <input
            type="number"
            className="parking-input"
            min="1"
            max="60"
            value={form.parking_duration_days}
            placeholder="e.g. 4 days"
            onChange={(e) => {
              setForm({
                ...form,
                parking_duration_days:
                  e.target.value === "" ? "" : Math.min(60, Math.max(1, Number(e.target.value))),
              });
            }}
            onBlur={() => {
              const num = Number(form.parking_duration_days);
              setForm({ ...form, parking_duration_days: Number.isNaN(num) ? 4 : Math.min(60, Math.max(1, num)) });
            }}
            style={{ padding: "9px 10px", fontSize: 14 }}
          />
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>Defaults to 4 days for EMA parking.</div>
        </div>

        <div className="form-group" style={{ marginBottom: 6 }}>
          <label>Flight number</label>
          <input
            className="parking-input"
            value={form.flight_number}
            placeholder="e.g. FR8683"
            onChange={(e) => {
              setForm({ ...form, flight_number: e.target.value });
              fetchFlightSuggestions(e.target.value);
            }}
            onFocus={() => setFlightFocused(true)}
            onBlur={() => setTimeout(() => setFlightFocused(false), 120)}
            style={{ padding: "9px 10px", fontSize: 14 }}
          />
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>Prefilled with a live EMA route (Ryanair FR8683).</div>
          {flightFocused && flightSuggestions.length > 0 && (
            <div className="suggestion-box">
              {flightSuggestions.map((s, idx) => (
                <div
                  key={`${s}-${idx}`}
                  className="suggestion-item"
                  onClick={() => {
                    setForm({ ...form, flight_number: s });
                    setFlightFocused(false);
                    setFlightSuggestions([]);
                    lookupFlight(s);
                  }}
                >
                  <div className="suggestion-main">{s}</div>
                  <div className="suggestion-sub">EMA flight</div>
                </div>
              ))}
            </div>
          )}
          {flightLookupError && <div style={{ color: "#b91c1c", fontSize: 12, marginTop: 4 }}>{flightLookupError}</div>}
        </div>

        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
          Destination locked to {TERMINAL_LABEL}.
        </div>

        <button onClick={submit} className="parking-btn">
          {loading ? "Comparing…" : "Find the best way"}
        </button>
        {error && <p style={{ color: "red", marginTop: "10px" }}>{error}</p>}
      </div>

      {renderLoading()}

      {result && candidateOptions.length > 0 && (
        <div className="parking-results">
          {renderHero()}

          <details
            className="train-collapsible"
            open={sectionOpen.leave}
            onToggle={(e) => setSectionOpen((prev) => ({ ...prev, leave: e.target.open }))}
            ref={sectionRefs.leave}
          >
            <summary className="train-summary-bar">📅 When to leave home (flight-aware)</summary>
            <div className="train-collapsible-body" style={{ fontSize: 13, color: "#475467" }}>
              {!leaveHomeAdvice && <div>Enter or select your flight to see tailored leave-home time.</div>}
              {leaveHomeAdvice && (
                <div>
                  <div style={{ fontWeight: 800, color: "#0f172a", marginBottom: 6 }}>
                    {leaveHomeAdvice.direction === "arrival" ? "Arrival" : "Departure"} flight: {form.flight_number || "—"}
                  </div>
                  <div
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 10,
                      padding: 12,
                      display: "grid",
                      gridTemplateColumns: "1.1fr 1.3fr",
                      gap: "6px 12px",
                      fontSize: 14,
                      background: "#f8fafc",
                    }}
                  >
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>Leave home by</div>
                    <div style={{ fontWeight: 700, color: "#b45309" }}>{leaveHomeAdvice.leaveBy || "—"}</div>

                    <div style={{ fontWeight: 700, color: "#0f172a" }}>Travel time to EMA</div>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>
                      ~{leaveHomeAdvice.travelMins} mins{" "}
                      {leaveHomeAdvice.travelLabel || leaveHomeAdvice.travelMode ? `(${leaveHomeAdvice.travelLabel || leaveHomeAdvice.travelMode})` : ""}
                    </div>

                    <div style={{ fontWeight: 700, color: "#0f172a" }}>Arrive at EMA by</div>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>{leaveHomeAdvice.airportBy || "—"}</div>

                    <div style={{ fontWeight: 700, color: "#0f172a" }}>
                      Scheduled {leaveHomeAdvice.direction === "arrival" ? "arrival" : "departure"}
                    </div>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>{leaveHomeAdvice.scheduled || "—"}</div>
                  </div>

                  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>
                    {leaveHomeAdvice.direction === "arrival"
                      ? "Arriving flight (meet on landing)."
                      : "Departing flight (arrive early)."}
                    {leaveHomeAdvice.bufferMins > 0 &&
                      " Includes a standard 2h30 pre-departure airport buffer (Source: industry-standard departure guidance)."}
                    {leaveHomeAdvice.targetSource ? ` Source: ${leaveHomeAdvice.targetSource.replace(/_/g, " ")}` : ""}
                  </div>
                  {leaveHomeAdvice.crossesMidnight && (
                    <div style={{ fontSize: 12, color: "#b45309", marginTop: 4 }}>
                      Times cross midnight — arrival is just after midnight.
                    </div>
                  )}
                </div>
              )}
            </div>
          </details>

          {renderFareCards()}

          <details
            className="train-collapsible"
            open={sectionOpen.hassle}
            onToggle={(e) => setSectionOpen((prev) => ({ ...prev, hassle: e.target.open }))}
          >
            <summary className="train-summary-bar">😌 Hassle & walking</summary>
            <div className="train-collapsible-body">
              <div style={{ fontSize: 13, color: "#475467", marginBottom: 8 }}>
                Bags, transfers, and changes are reflected in the hassle badge. Transfers from East Midlands Parkway are highlighted.
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px,1fr))", gap: 10 }}>
                {candidateOptions.map((opt) => {
                  const walk = walkFor(opt);
                  const changes = changesFor(opt);
                  const duration = durationFor(opt);
                  const hassle = displayHassle(opt, hassleFor(opt));
                  const bus =
                    (opt?.breakdown?.price_only_reasons || []).includes("includes_bus") || opt?.breakdown?.includes_bus;
                  const isDrive = opt.mode === "drive";
                  const transferType = (opt.breakdown?.transfer_type || "").toLowerCase();
                  const transferIcon = transferType.includes("shuttle") ? "🚌" : "🚶";
                  const walkLabel = isDrive
                    ? walk
                      ? `${transferIcon} ${walk} min walk from parking`
                      : "🚗 Drive + park"
                    : walk
                    ? `🚶 ${walk} min walk`
                    : "🚪 Door-to-terminal";
                  const driveShuttle = isDrive && transferType.includes("shuttle");
                  return (
                    <div key={opt.id} style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 12, background: "#fff" }}>
                      <div style={{ fontWeight: 800 }}>{opt.id === "taxi_ccg" ? "Taxi" : opt.label}</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>
                {hassle.emoji} {hassle.label}
              </div>
                      <div style={{ fontSize: 12, color: "#475467", marginTop: 6 }}>
                        {walkLabel} • {changes ? `${changes} change` : "0 changes"} • {duration ? `${durationLabel(opt)}` : "—"}
                      </div>
                      {(bus || driveShuttle) && (
                        <div style={{ fontSize: 12, color: "#b45309", marginTop: 6 }}>
                          🚌 {driveShuttle ? "Shuttle from car park" : "Includes bus transfer"}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </details>

          <details
            className="train-collapsible"
            open={sectionOpen.how}
            onToggle={(e) => setSectionOpen((prev) => ({ ...prev, how: e.target.open }))}
          >
            <summary className="train-summary-bar">ℹ️ How we calculate</summary>
            <div className="train-collapsible-body">
              <div style={{ fontSize: 13, color: "#475467", marginBottom: 8 }}>
                Taxi vs train + Parkway transfer, with walking, changes and luggage hassle factored in — now includes self-drive (fuel + parking).
                Myth buster: for groups + bags, train + transfers can cost more (and feel harder) than a fixed taxi.
                Note: East Midlands Airport has no on-site rail station. Train routes usually include a transfer via East Midlands Parkway.
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                <li>TfL fare: live journey + surcharge where applicable.</li>
                <li>National Rail fares from licensed Rail Data Marketplace (live where available), via East Midlands Parkway.</li>
                <li>CheapCabGrab uses live preview pricing; ride-hail/black cab are estimates.</li>
                <li>Self-drive combines OSRM distance (fuel at 45 mpg, £1.45/litre) plus best EMA parking quote for your stay.</li>
                <li>Cheapest badge stays on the lowest price; Gold highlights best overall (cost + hassle).</li>
              </ul>
            </div>
          </details>
        </div>
      )}

      {result && candidateOptions.length > 0 && <EmaLiveOpsSection />}

      <div style={{ marginTop: 16, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#f9fafb" }}>
        <div style={{ fontWeight: 800, marginBottom: 6 }}>Quick tips</div>
        <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
          <li>EMA has no on-airport rail station — Parkway + transfer is standard.</li>
          <li>Cheapest isn’t always simplest; look at hassle before choosing.</li>
          <li>Groups with bags often find taxi best overall, even if train is cheapest.</li>
        </ul>
      </div>

      <div style={{ marginTop: 14, border: "1px solid #bfdbfe", borderRadius: 14, padding: 14, background: "#eff6ff" }}>
        <div style={{ fontWeight: 800, color: "#0f172a", marginBottom: 6 }}>Plan your East Midlands Airport journey</div>
        <div style={{ fontSize: 14, color: "#1e3a8a", lineHeight: 1.55 }}>
          For live leave times, disruption monitoring and a clearer recommended option, use the
          {" "}
          <a href="/east-midlands-airport/live-journey-planner" style={{ fontWeight: 800 }}>
            East Midlands Airport Live Journey Planner
          </a>
          .
        </div>
        <div style={{ marginTop: 10, lineHeight: 1.7, color: "#1f2937" }}>
          <div><a href="/east-midlands-airport/live-journey-planner">Live journey planner</a></div>
          <div><a href="/east-midlands-airport/london-to-east-midlands-airport">London to East Midlands Airport</a></div>
          <div><a href="/east-midlands-airport/birmingham-to-east-midlands-airport">Birmingham to East Midlands Airport</a></div>
          <div><a href="/east-midlands-airport/return-journey-monitoring">Return journey monitoring</a></div>
          <div><a href="/east-midlands-airport/when-to-leave-for-east-midlands-airport">When to leave for East Midlands Airport</a></div>
          <div><a href="/train-vs-taxi-east-midlands-airport">Train vs taxi to East Midlands Airport</a></div>
          <div><a href="/east-midlands-airport/drop-off-calculator">EMA drop-off calculator</a></div>
        </div>
      </div>

      <div style={{ marginTop: "32px" }}>
        <ReviewsBar />
      </div>
    </div>
  );
}
